import { createFocusTrap } from "focus-trap";
import { listen, qs, qsa } from "@fluorescent/dom";
import updateInnerHTML from "@/lib/update-inner-html";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import getMediaQuery from "@/lib/media-queries";

import FilterForm from "@/lib/filters/filter-form";

const selectors = {
  drawer: "[data-filter-drawer]",
  drawerWash: "[data-filter-drawer-wash]",
  drawerForm: "[data-filter-form]",
  drawerToggle: "[data-drawer-toggle]",
  drawerTitle: "[data-filter-drawer-title]",
  drawerApply: "[data-filter-drawer-apply]",
  closeDrawer: "[data-close-drawer]",
  clearAll: "[data-clear-all-filters]",
};

const FilterDrawer = node => {
  if (!node) return false;

  const drawerEl = qs(selectors.drawer, node);

  if (!drawerEl) return false;

  const drawerFormEl = qs(selectors.drawerForm, drawerEl);
  const filterForm = FilterForm(drawerFormEl);
  const drawerToggleEl = qs(selectors.drawerToggle, node);
  const drawerCloseEls = qsa(
    `${selectors.closeDrawer}, ${selectors.drawerApply}`,
    drawerEl
  );

  const clearAllEl = qs(selectors.clearAll, drawerEl);
  const drawerWash = qs(selectors.drawerWash, node);

  let focusTrap = createFocusTrap(drawerEl, {
    allowOutsideClick: true,
  });

  const events = [
    listen(clearAllEl, "click", () => {
      filterForm?.clearAllFilters();
    }),
    listen(drawerWash, "click", hideDrawer),
    listen(drawerCloseEls, "click", hideDrawer),
    listen(drawerFormEl, "keydown", ({ keyCode }) => {
      if (keyCode === 27) hideDrawer();
    }),
  ];

  function toggleDrawer() {
    const ariaHidden = drawerEl.getAttribute("aria-hidden") === "true";

    if (ariaHidden) {
      showDrawer();
    } else {
      hideDrawer();
    }
  }

  function showDrawer() {
    if (drawerToggleEl) {
      drawerToggleEl.setAttribute("aria-expanded", true);
    }
    drawerEl.setAttribute("aria-hidden", false);

    if (window.matchMedia(getMediaQuery("below-1024")).matches) {
      focusTrap.activate();
      disableBodyScroll(node, {
        allowTouchMove: el => {
          while (el && el !== document.body) {
            if (el.getAttribute("data-scroll-lock-ignore") !== null) {
              return true;
            }
            el = el.parentNode;
          }
        },
        reserveScrollBarGap: true,
      });
    }
  }

  function hideDrawer() {
    if (drawerToggleEl) {
      drawerToggleEl.setAttribute("aria-expanded", false);
    }
    drawerEl.setAttribute("aria-hidden", true);

    if (window.matchMedia(getMediaQuery("below-1024")).matches) {
      focusTrap?.deactivate();
      enableBodyScroll(node);
    }
  }

  function renderFilters(doc) {
    filterForm.renderFilters(doc);

    updateInnerHTML(`${selectors.drawer} ${selectors.drawerTitle}`, doc);
    updateInnerHTML(`${selectors.drawer} ${selectors.drawerApply}`, doc);

    const docDrawerEl = qs(selectors.drawer, doc);

    drawerEl.dataset.filtersActive = docDrawerEl.dataset.filtersActive;
  }

  function unload() {
    events.forEach(unsubscribe => unsubscribe());
    filterForm && filterForm.unload();
  }

  return {
    toggleDrawer,
    renderFilters,
    unload,
  };
};

export default FilterDrawer;
